import { format, formatDistance, parseISO } from 'date-fns';

export const formatCurrency = (value: number | undefined): string => {
  if (value === undefined || value === null) return '-';
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

/**
 * Computes the total PP earnings including setup and print costs from the per-outdoor breakdown.
 * Returns null when the base value is absent so callers can gate rendering on it.
 */
export const totalPPEarnings = (campaign: {
  valueForPlacementProviderInCurrency?: number | null;
  placementProviderCampaignOutdoorCosts?: Array<{
    earningsInCurrency?: number | null;
  }> | null;
}): number | null => {
  const costs = campaign.placementProviderCampaignOutdoorCosts;
  // When the detailed breakdown is present, earningsInCurrency on each outdoor
  // already represents the full amount (including setup & print), so just sum those.
  if (costs != null && costs.length > 0) {
    return costs.reduce((sum, c) => sum + (c.earningsInCurrency ?? 0), 0);
  }
  // Fallback for list-view responses that only carry the aggregate field.
  return campaign.valueForPlacementProviderInCurrency ?? null;
};

/** Format a currency value with an explicit ISO 4217 currency code (e.g. "EUR", "USD"). */
export const formatCurrencyWithCode = (
  value: number | undefined | null,
  currency: string | undefined | null,
): string => {
  if (value === undefined || value === null) return '-';
  const code = currency ?? 'EUR';
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: code,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  } catch {
    return `${code} ${value.toFixed(2)}`;
  }
};

export const formatDate = (date: string | undefined): string => {
  if (!date) return '-';
  try {
    return format(parseISO(date), 'dd MMM yyyy');
  } catch {
    return date;
  }
};

export const formatDateTime = (date: string | undefined): string => {
  if (!date) return '-';
  try {
    return format(parseISO(date), 'dd MMM yyyy, HH:mm');
  } catch {
    return date;
  }
};

export const formatDateRange = (start?: string, end?: string): string => {
  if (!start || !end) return '-';
  return `${formatDate(start)} – ${formatDate(end)}`;
};

export const formatRelative = (date: string | undefined): string => {
  if (!date) return '-';
  try {
    return formatDistance(parseISO(date), new Date(), { addSuffix: true });
  } catch {
    return date;
  }
};

/**
 * Today's date in the viewer's local timezone, as YYYY-MM-DD.
 * Use this instead of `new Date().toISOString().split('T')[0]` for "today" comparisons -
 * `toISOString()` converts to UTC first, so in positive-UTC-offset timezones (e.g. Western
 * Europe), anywhere from midnight up to the offset (e.g. up to 1am in UTC+1) it still reports
 * yesterday's date, incorrectly blocking date-gated actions (like verifying a display) that
 * should already be allowed.
 */
export const todayDateString = (): string => format(new Date(), 'yyyy-MM-dd');

export const toDateInputValue = (date: string | undefined): string => {
  if (!date) return '';
  try {
    return format(parseISO(date), 'yyyy-MM-dd');
  } catch {
    return '';
  }
};

/**
 * Number of days a booking from `start` to `end` covers, inclusive of both ends -
 * e.g. Sep 10 to Sep 14 is 5 days (10, 11, 12, 13, 14), not the 4-day gap between
 * them. This is the billed/rented day count throughout the app (the outdoor is
 * displayed on both the start and end date, not "checked out" on the end date
 * like a hotel stay) - always use this instead of re-deriving the raw day gap,
 * which undercounts by one and has already caused real bugs (an advertiser's
 * exact-minimum-length booking being rejected because the raw gap came up one
 * short of the minimum).
 */
export const daysBetweenInclusive = (start: string, end: string): number =>
  Math.round((new Date(end).getTime() - new Date(start).getTime()) / (1000 * 60 * 60 * 24)) + 1;

export const formatDimensions = (w?: number, h?: number, l?: number): string => {
  const parts: string[] = [];
  if (w) parts.push(`${w}m W`);
  if (h) parts.push(`${h}m H`);
  if (l) parts.push(`${l}m L`);
  return parts.join(' × ') || '-';
};

export const formatAspectRatio = (ratio?: number): string => {
  if (!ratio) return '-';
  const gcd = (a: number, b: number): number => (b === 0 ? a : gcd(b, a % b));
  const scale = 100;
  const w = Math.round(ratio * scale);
  const h = scale;
  const d = gcd(w, h);
  return `${w / d}:${h / d}`;
};

/** Returns the highest applicable discount percentage (0-100) for the given number of days. */
export const bestDiscount = (
  discounts: Array<{ percentage?: number; moreThanNumberOfDays?: number }> | undefined,
  days: number
): number => {
  if (!discounts?.length || days <= 0) return 0;
  const applicable = discounts.filter((d) => days > (d.moreThanNumberOfDays ?? 0));
  if (!applicable.length) return 0;
  return Math.max(...applicable.map((d) => d.percentage ?? 0));
};

export const decodeJwt = (token: string): Record<string, unknown> | null => {
  try {
    return JSON.parse(atob(token.split('.')[1]));
  } catch {
    return null;
  }
};
